import {action, makeAutoObservable, observable} from "mobx";

export interface SequenceUserClip {
    id: string;
    type: string;
    startTime: number;
    duration: number;
    trimmedDuration: number;
    hasPredecessor?: boolean;
    hasSuccessor?: boolean;
    waveformColor?: string; // Color for waveform display
}

export interface SequenceLayer {
    id: string;
    clipIds: string[];
    name?: string;
}

class SequenceStateStoreInstance {
    isPlaying: boolean = false;
    currentTime: number = 0;
    orderedLayerIds: string[] = []; // topmost first
    clips: Record<string, SequenceUserClip> = {};
    layers: Record<string, SequenceLayer> = {};
    clipIdToAssetId: Record<string, string> = {}; // clipId -> trackId/assetId for waveform fetching
    clipIdToThumbnailId: Record<string, string> = {};
    clipIdToTitle: Record<string, string> = {};
    clipIdToMediaResolution: Record<string, {width: number, height: number}> = {};
    assetIdToClips: Record<string, string[]> = {};
    clipIdToWaveformColor: Record<string, string> = {}; // clipId -> waveform color
    duration: number = 0; // seconds
    display = {
        width: 1920,
        height: 1080,
        backgroundColor: "#000000",
    }

    constructor() {
        makeAutoObservable(this, {
            setIsPlaying: action,
            setCurrentTime: action,
            addLayer: action,
            removeLayer: action,
            addClip: action,
            updateClip: action,
            removeClip: action,
            setDisplayResolution: action,
            setDuration: action,
            setAssetIdForClip: action,
            setLayerName: action,
            setWaveformColor: action,
            orderedLayerIds: observable,
            display: observable,
            layers: observable,
        });
    }

    setIsPlaying(isPlaying: boolean) {
        this.isPlaying = isPlaying;
    }

    getClipTitle(clipId: string) {
        return this.clipIdToTitle[clipId] ?? clipId;
    }

    setCurrentTime(currentTime: number) {
        this.currentTime = currentTime;
    }

    setDisplayResolution(width: number, height: number) {
        this.display.width = width;
        this.display.height = height;
    }

    setDuration(seconds: number) {
        this.duration = Math.max(0, Number(seconds) || 0);
    }

    addLayer(layer: SequenceLayer) {
        this.layers[layer.id] = layer;
        this.orderedLayerIds.push(layer.id);
    }

    removeLayer(layerId: string) {
        const layer = this.layers[layerId];
        if (!layer) return;
        this.orderedLayerIds = this.orderedLayerIds.filter(id => id !== layerId);
        delete this.layers[layerId];
    }

    addClip(layerId: string, clip: SequenceUserClip) {
        this.clips[clip.id] = clip;
        if (this.layers[layerId]) {
            this.layers[layerId].clipIds.push(clip.id);
        } else {
            this.layers[layerId] = {id: layerId, clipIds: [clip.id]};
            this.orderedLayerIds.push(layerId);
        }
    }

    updateClip(clipId: string, clip: SequenceUserClip) {
        this.clips[clipId] = clip;
    }

    removeClip(layerId: string, clipId: string) {
        if (this.layers[layerId]) {
            this.layers[layerId].clipIds = this.layers[layerId].clipIds.filter(id => id !== clipId);
        }
        delete this.clips[clipId];
        delete this.clipIdToAssetId[clipId];
    }

    setAssetIdForClip(clipId: string, assetId: string) {
        this.clipIdToAssetId[clipId] = assetId;
        if (!this.assetIdToClips[assetId]) {
            this.assetIdToClips[assetId] = [];
        }
        this.assetIdToClips[assetId].push(clipId);
    }

    setTitleForClip(clipId: string, filename: string) {
        this.clipIdToTitle[clipId] = filename;
    }

    setOriginalMediaResolutionForClip(clipId: string, width: number, height: number) {
        this.clipIdToMediaResolution[clipId] = {width, height};
    }

    setLayerName(layerId: string, name: string) {
        if (this.layers[layerId]) {
            this.layers[layerId].name = name;
        }
    }

    getLayerName(layerId: string): string | undefined {
        return this.layers[layerId]?.name;
    }

    setWaveformColor(clipId: string, color: string) {
        this.clipIdToWaveformColor[clipId] = color;
        // Also update the clip object if it exists
        if (this.clips[clipId]) {
            this.clips[clipId].waveformColor = color;
        }
    }

    setThumbnailIdForClip(clipId: string, thumbnailId: string) {
        this.clipIdToThumbnailId[clipId] = thumbnailId;
    }

    getWaveformColor(clipId: string): string | undefined {
        return this.clipIdToWaveformColor[clipId] || this.clips[clipId]?.waveformColor;
    }

    reset() {
        this.isPlaying = false;
        this.currentTime = 0;
        this.orderedLayerIds = [];
        this.clips = {};
        this.layers = {};
        this.clipIdToAssetId = {};
        this.clipIdToTitle = {};
        this.clipIdToMediaResolution = {};
        this.assetIdToClips = {};
        this.clipIdToWaveformColor = {};
        this.duration = 0;
        this.display = {
            width: 1920,
            height: 1080,
            backgroundColor: "#000000",
        };
    }
}

export const SequenceStateStore = new SequenceStateStoreInstance();


